import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { SponsorshipDto, SponsorshipKind } from '@rw/shared';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { Sheet } from '../../components/Sheet';
import { LoadingState } from '../../components/states';
import { Button, Card } from '../../components/ui';
import { useModerationEvents } from '../../hooks/queries';
import { ApiError, api } from '../../lib/api';
import { formatDate } from '../../lib/format';
import { toast } from '../../stores/toasts';
import { useT } from '../../i18n';
import { useCampaigns } from '../campaigns/campaigns-api';
import { useBrands } from './brands-api';
import { formatVnd, ledgerKeys, useLedger } from './reports-api';

const input = 'mt-1 h-11 w-full rounded-xl border border-ink-700 bg-ink-850 px-3 outline-none focus:border-volt';
const KIND_LABEL: Record<SponsorshipKind, string> = { CASH: 'Cash', IN_KIND: 'Goods' };

/** Game owner: what each brand pledged (cash or goods), whether it arrived, and its report. */
export function LedgerTab() {
  const q = useLedger();
  const brands = useBrands();
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [brandId, setBrandId] = useState('');
  const [editing, setEditing] = useState<SponsorshipDto | 'new' | null>(null);
  const t = useT();
  const received = useMutation({
    mutationFn: (v: { id: string; received: boolean }) => api<SponsorshipDto>(`/admin/sponsorships/${v.id}`, { method: 'PATCH', body: { received: v.received } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ledgerKeys.all }),
  });
  if (!q.data) return <LoadingState />;
  const rows = q.data.filter((s) => !brandId || s.brand.id === brandId);
  const cash = rows.filter((s) => s.kind === 'CASH');
  const sum = (list: SponsorshipDto[]) => list.reduce((t, s) => t + s.amountVnd, 0);
  return (
    <div className="mt-4 space-y-3" data-testid="admin-ledger">
      <div className="flex items-center gap-2">
        <select value={brandId} onChange={(e) => setBrandId(e.target.value)} className={`${input} mt-0 flex-1`} data-testid="ledger-brand-filter">
          <option value="">{t('All brands')}</option>
          {brands.data?.map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
        <Button size="sm" onClick={() => setEditing('new')} data-testid="ledger-new">
          {t('+ New')}
        </Button>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <Total label={t('Cash pledged')} value={formatVnd(sum(cash))} />
        <Total label={t('Cash received')} value={formatVnd(sum(cash.filter((s) => s.receivedAt)))} testId="ledger-received-total" />
        <Total label={t('Goods (value)')} value={formatVnd(sum(rows.filter((s) => s.kind === 'IN_KIND')))} />
      </div>
      {brandId ? (
        <Button variant="secondary" className="w-full" onClick={() => navigate(`/admin/reports/${brandId}`)} data-testid="ledger-open-report">
          {t('Report for {name} →', { name: brands.data?.find((b) => b.id === brandId)?.name ?? '' })}
        </Button>
      ) : null}
      {rows.length === 0 ? <p className="text-sm text-ink-500">{t('Nothing recorded yet.')}</p> : null}
      {rows.map((s) => (
        <div key={s.id} data-testid="ledger-row">
          <Card className="space-y-1">
            <div className="flex items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="truncate font-semibold">
                  {s.brand.name} · {s.kind === 'CASH' ? formatVnd(s.amountVnd) : s.items}
                </div>
                <div className="text-xs text-ink-400">
                  {t(KIND_LABEL[s.kind])}
                  {s.kind === 'IN_KIND' && s.amountVnd ? ` · ≈ ${formatVnd(s.amountVnd)}` : ''} · {t('pledged {date}', { date: formatDate(s.pledgedAt) })}
                  {s.event ? ` · ${t('event: {name}', { name: s.event.name })}` : ''}
                  {s.campaign ? ` · ${t('campaign: {name}', { name: s.campaign.name })}` : ''}
                </div>
                {s.note ? <div className="text-xs text-ink-300">{s.note}</div> : null}
              </div>
              <button onClick={() => setEditing(s)} className="shrink-0 text-xs font-semibold text-volt">
                {t('Edit')}
              </button>
            </div>
            <label className="flex items-center gap-2 text-sm font-semibold">
              <input
                type="checkbox"
                checked={received.isPending && received.variables.id === s.id ? received.variables.received : !!s.receivedAt}
                onChange={(e) => received.mutate({ id: s.id, received: e.target.checked })}
                className="size-4"
                data-testid="ledger-received"
              />
              {s.receivedAt ? t('Received {date}', { date: formatDate(s.receivedAt) }) : t('Received')}
            </label>
          </Card>
        </div>
      ))}
      <SponsorshipSheet entry={editing} defaultBrandId={brandId} onClose={() => setEditing(null)} />
    </div>
  );
}

function Total({ label, value, testId }: { label: string; value: string; testId?: string }) {
  return (
    <div className="rounded-xl bg-ink-850 px-2 py-2">
      <div className="text-sm font-bold tabular" data-testid={testId}>
        {value}
      </div>
      <div className="text-[11px] text-ink-400">{label}</div>
    </div>
  );
}

function SponsorshipSheet({ entry, defaultBrandId, onClose }: { entry: SponsorshipDto | 'new' | null; defaultBrandId: string; onClose: () => void }) {
  const qc = useQueryClient();
  const brands = useBrands(!!entry);
  const campaigns = useCampaigns();
  const events = useModerationEvents();
  const existing = entry && entry !== 'new' ? entry : null;
  const [form, setForm] = useState<Record<string, string>>({});
  const [received, setReceived] = useState(false);
  const t = useT();
  const key = existing?.id ?? 'new';
  const [formKey, setFormKey] = useState<string | null>(null);
  if (entry && formKey !== key) {
    setFormKey(key);
    setForm({
      brandId: existing?.brand.id ?? defaultBrandId,
      kind: existing?.kind ?? 'CASH',
      amountVnd: existing ? String(existing.amountVnd) : '',
      items: existing?.items ?? '',
      link: existing?.event ? `event:${existing.event.id}` : existing?.campaign ? `campaign:${existing.campaign.id}` : '',
      pledgedAt: existing?.pledgedAt ?? new Date().toISOString().slice(0, 10),
      note: existing?.note ?? '',
    });
    setReceived(!!existing?.receivedAt);
  }
  const close = () => {
    setFormKey(null);
    onClose();
  };
  const [linkKind, linkId] = (form.link ?? '').split(':');
  const body = {
    kind: form.kind,
    amountVnd: Number((form.amountVnd ?? '').replace(/\D/g, '')) || 0,
    items: form.items ?? '',
    eventId: linkKind === 'event' ? linkId : null,
    campaignId: linkKind === 'campaign' ? linkId : null,
    pledgedAt: form.pledgedAt,
    received,
    note: form.note ?? '',
  };
  const save = useMutation({
    mutationFn: () =>
      existing
        ? api<SponsorshipDto>(`/admin/sponsorships/${existing.id}`, { method: 'PATCH', body })
        : api<SponsorshipDto>('/admin/sponsorships', { body: { ...body, brandId: form.brandId } }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ledgerKeys.all });
      toast({ kind: 'success', title: t('Saved') });
      close();
    },
    onError: (e) => toast({ kind: 'error', title: e instanceof ApiError ? e.message : t('Could not save') }),
  });
  const remove = useMutation({
    mutationFn: () => api(`/admin/sponsorships/${existing!.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ledgerKeys.all });
      toast({ kind: 'success', title: t('Deleted') });
      close();
    },
  });
  const set = (k: string) => (e: { target: { value: string } }) => setForm({ ...form, [k]: e.target.value });
  const isCash = form.kind === 'CASH';
  const valid = !!form.brandId && !!form.pledgedAt && (isCash ? body.amountVnd > 0 : body.items.trim().length > 0);
  const brandCampaigns = (campaigns.data ?? []).filter((c) => c.brand.id === form.brandId);
  return (
    <Sheet open={!!entry} onClose={close} label={t('Sponsorship')}>
      {entry ? (
        <div className="space-y-3" data-testid="ledger-sheet">
          <h2 className="font-display text-2xl font-bold">{t(existing ? 'Edit entry' : 'New entry')}</h2>
          <label className="block text-sm font-semibold">
            {t('Brand')}
            <select value={form.brandId} disabled={!!existing} onChange={set('brandId')} className={input} data-testid="ledger-brand">
              <option value="">{t('Choose a brand…')}</option>
              {brands.data?.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          </label>
          <div className="flex gap-1 rounded-xl bg-ink-850 p-1">
            {(['CASH', 'IN_KIND'] as const).map((k) => (
              <button
                key={k}
                onClick={() => setForm({ ...form, kind: k })}
                className={`h-9 flex-1 rounded-lg text-sm font-bold ${form.kind === k ? 'bg-volt text-ink-950' : 'text-ink-300'}`}
                data-testid={`ledger-kind-${k}`}
              >
                {t(KIND_LABEL[k])}
              </button>
            ))}
          </div>
          {isCash ? null : (
            <label className="block text-sm font-semibold">
              {t('Goods (what and how many)')}
              <input value={form.items ?? ''} onChange={set('items')} placeholder={t('30 finisher shirts')} className={input} data-testid="ledger-items" />
            </label>
          )}
          <label className="block text-sm font-semibold">
            {t(isCash ? 'Amount (VND)' : 'Estimated value (VND, optional)')}
            <input inputMode="numeric" value={form.amountVnd ?? ''} onChange={set('amountVnd')} className={input} data-testid="ledger-amount" />
            {body.amountVnd ? <span className="text-xs text-ink-400">{formatVnd(body.amountVnd)}</span> : null}
          </label>
          <label className="block text-sm font-semibold">
            {t('For')}
            <select value={form.link} onChange={set('link')} className={input} data-testid="ledger-link">
              <option value="">{t('— (the brand in general)')}</option>
              {brandCampaigns.map((c) => (
                <option key={c.id} value={`campaign:${c.id}`}>
                  {t('Campaign: {name}', { name: c.name })}
                </option>
              ))}
              {(events.data ?? []).map((e) => (
                <option key={e.id} value={`event:${e.id}`}>
                  {t('Event: {name}', { name: e.name })}
                </option>
              ))}
            </select>
          </label>
          <label className="block text-sm font-semibold">
            {t('Pledged on')}
            <input type="date" value={form.pledgedAt ?? ''} onChange={set('pledgedAt')} className={input} data-testid="ledger-date" />
          </label>
          <label className="block text-sm font-semibold">
            {t('Note')}
            <textarea value={form.note ?? ''} onChange={set('note')} rows={2} className={`${input} h-auto py-2`} />
          </label>
          <label className="flex items-center gap-3 text-sm font-semibold">
            <input type="checkbox" checked={received} onChange={(e) => setReceived(e.target.checked)} className="size-5" />
            {t('Received')}
          </label>
          <div className="flex gap-2">
            {existing ? (
              <Button variant="secondary" onClick={() => remove.mutate()} loading={remove.isPending}>
                {t('Delete')}
              </Button>
            ) : null}
            <Button className="flex-1" loading={save.isPending} disabled={!valid} onClick={() => save.mutate()} data-testid="ledger-save">
              {t('Save')}
            </Button>
          </div>
        </div>
      ) : null}
    </Sheet>
  );
}
